// UK-aware phone helpers. Server and web both use these so dial-time validation
// matches what compliance checks see in storage.

const UK_AREA_CODES_BY_PREFIX: Array<[string, string]> = [
  ['+4420', '020'],
  ['+44121', '0121'],
  ['+44131', '0131'],
  ['+44141', '0141'],
  ['+44151', '0151'],
  ['+44161', '0161'],
  ['+44113', '0113'],
  ['+44114', '0114'],
  ['+44115', '0115'],
  ['+44116', '0116'],
  ['+44117', '0117'],
  ['+44118', '0118'],
  ['+44191', '0191'],
  ['+447', '07'],
  ['+443', '03'],
  ['+44800', '0800'],
];

export function normalizePhoneToE164(input: string, defaultCountry: 'GB' = 'GB'): string | null {
  if (!input) return null;
  const cleaned = input.replace(/[\s()\-.]/g, '');

  if (/^\+\d{6,15}$/.test(cleaned)) return cleaned;

  if (defaultCountry === 'GB') {
    if (/^00\d+$/.test(cleaned)) {
      return '+' + cleaned.slice(2);
    }
    if (/^0\d{9,10}$/.test(cleaned)) {
      return '+44' + cleaned.slice(1);
    }
    if (/^44\d{8,11}$/.test(cleaned)) {
      return '+' + cleaned;
    }
  }

  return null;
}

export function isValidUKPhoneNumber(input: string): boolean {
  const e164 = normalizePhoneToE164(input);
  if (!e164) return false;
  return /^\+44\d{9,10}$/.test(e164);
}

export function isUKMobile(e164: string): boolean {
  return /^\+447\d{9}$/.test(e164);
}

export function extractUKAreaCode(e164: string): string | null {
  for (const [prefix, areaCode] of UK_AREA_CODES_BY_PREFIX) {
    if (e164.startsWith(prefix)) return areaCode;
  }
  return null;
}

export function formatUKForDisplay(e164: string): string {
  if (!e164.startsWith('+44')) return e164;
  const rest = '0' + e164.slice(3);
  if (rest.startsWith('020')) return rest.replace(/^(020)(\d{4})(\d{4})$/, '$1 $2 $3');
  if (rest.startsWith('07')) return rest.replace(/^(07\d{3})(\d{6})$/, '$1 $2');
  if (rest.startsWith('0800')) return rest.replace(/^(0800)(\d{6,7})$/, '$1 $2');
  return rest.replace(/(\d{4})(\d{3})(\d+)/, '$1 $2 $3');
}

// Every textual form a UK number might be stored as on the CRM side. Perfex
// keeps `phonenumber` exactly as a human typed it ("07404040402", "0740 4040
// 402", "447404040402") while the dialler always canonicalises to E.164, so a
// single-string lookup against ho.marn.uk silently misses and we mint a
// duplicate record. Query with all of these before deciding nothing matched.
export function phoneMatchVariants(input: string): string[] {
  const e164 = normalizePhoneToE164(input);
  if (!e164) return [];
  const variants = new Set<string>([e164]);
  if (e164.startsWith('+44')) {
    variants.add(e164.slice(1));        // 447404040402
    variants.add('0' + e164.slice(3));  // 07404040402
    variants.add('00' + e164.slice(1)); // 00447404040402
  }
  return [...variants];
}

// Format-agnostic identity key for a phone number: the last 9 significant
// digits, which are identical across +447…/07…/447…/00447… forms. Use for
// cache keys and for grouping records that are the same number written
// differently. Returns null when there aren't enough digits to be meaningful.
export function phoneMatchKey(input: string): string | null {
  const e164 = normalizePhoneToE164(input);
  const digits = (e164 ?? input).replace(/\D/g, '');
  return digits.length >= 9 ? digits.slice(-9) : null;
}
